// src/app/modules/demandes/HorsCatalogue.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "../../RessourcesStyles/HorsCatalogue.css";
import "../../RessourcesStyles/DemandeFormModal.css";

import { fetchVisibleCampagnesRequest } from "../../../../../redux/actions/campagnesActions";
import { fetchCataloguesRequest } from "../../../../../redux/actions/cataloguesActions";
import { fetchDomainesRequest, fetchThemesRequest } from "../../../../../redux/actions/paramDomainesThemesActions";
import { clearDemandeMessage, createDemandeCollabRequest } from "../../../../../redux/actions/demandesActions";

const initial = {
    campagneId: "",
    domaineId: "",
    themeId: "",
    action: "",
    description: "",
    mode: "",
    period: "",
    niveau: "",
    certifiante: "",
    priorite: 2,
    objectifs: "",
    objectifs2: "",
    objectifs3: "",
};

const PERIOD_OPTIONS = ["Q1", "Q2", "Q3", "Q4"];
const NIVEAU_OPTIONS = ["Débutant", "Intermédiaire", "Avancé"];
const MODE_OPTIONS = ["Distanciel", "Présentiel", "Hybride"];

export default function HorsCatalogue({
    campagneId: campagneIdProp = null,
    hideCampagneSelect = false,
    onCancel,
    onSuccess,
    loading: loadingProp = false,
}) {
    const dispatch = useDispatch();

    const campagnesState = useSelector((s) => s.campagnes || {});
    const paramdomainethemes = useSelector((s) => s.paramDomainesThemes || {});
    const demandesState = useSelector((s) => s.demandes || {});

    const campagnes = campagnesState.visibleList || campagnesState.list || [];
    const domaines = paramdomainethemes.domaines || [];
    const themes = paramdomainethemes.themes || [];

    const [form, setForm] = useState(initial);
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const loading = !!(loadingProp || demandesState.loading);

    useEffect(() => {
        dispatch(fetchVisibleCampagnesRequest());
        dispatch(fetchCataloguesRequest());
        dispatch(fetchDomainesRequest());
        dispatch(fetchThemesRequest());
        dispatch(clearDemandeMessage());
    }, [dispatch]);

    useEffect(() => {
        if (campagneIdProp != null) return;
        if (!form.campagneId && campagnes?.length) {
            setForm((state) => ({ ...state, campagneId: String(campagnes[0].id) }));
        }
    }, [campagnes, form.campagneId, campagneIdProp]);

    useEffect(() => {
        if (!submitAttempted || loading) return;

        if (demandesState.error) {
            setSubmitAttempted(false);
            return;
        }

        if (demandesState.message) {
            setForm((state) => ({
                ...initial,
                campagneId: hideCampagneSelect && campagneIdProp ? String(campagneIdProp) : state.campagneId,
            }));
            setSubmitAttempted(false);
            dispatch(clearDemandeMessage());
            if (onSuccess) onSuccess();
        }
    }, [
        campagneIdProp,
        demandesState.error,
        demandesState.message,
        dispatch,
        hideCampagneSelect,
        loading,
        onSuccess,
        submitAttempted,
    ]);

    const campagneId = campagneIdProp ?? (form.campagneId ? Number(form.campagneId) : null);

    const themesFiltered = useMemo(() => {
        const did = form.domaineId ? Number(form.domaineId) : null;
        const list = Array.isArray(themes) ? themes : [];
        if (!did) return list;
        return list.filter((theme) => Number(theme?.domaineId || theme?.domaine?.id) === did);
    }, [themes, form.domaineId]);

    const update = (key, value) => setForm((state) => ({ ...state, [key]: value }));

    const submit = (event) => {
        event.preventDefault();

        if (!campagneId) {
            Swal.fire({ icon: "warning", text: "Sélectionnez d'abord une campagne." });
            return;
        }
        if (!form.action?.trim()) {
            Swal.fire({ icon: "warning", text: "L'intitulé de la formation est obligatoire." });
            return;
        }

        const payload = {
            campagneId,
            type: "HORS_CATALOGUE",
            domaineId: form.domaineId ? Number(form.domaineId) : null,
            themeId: form.themeId ? Number(form.themeId) : null,
            action: form.action.trim(),
            description: form.description?.trim() || null,
            mode: form.mode || null,
            period: form.period || null,
            niveau: form.niveau || null,
            certifiante: form.certifiante === "true" ? true : form.certifiante === "false" ? false : null,
            priorite: form.priorite ? Number(form.priorite) : null,
            objectifs: form.objectifs || null,
            objectifs2: form.objectifs2 || null,
            objectifs3: form.objectifs3 || null,
        };

        dispatch(clearDemandeMessage());
        setSubmitAttempted(true);
        dispatch(createDemandeCollabRequest(payload));
    };

    return (
        <form className="demande-form" onSubmit={submit}>
            <div className="field">
                <label>Intitulé de la formation *</label>
                <input
                    value={form.action}
                    onChange={(event) => update("action", event.target.value)}
                    placeholder="Saisissez l'intitulé de la formation"
                />
            </div>

            <div className="demande-grid-2">
                <div className="field">
                    <label>Domaine</label>
                    <select
                        value={form.domaineId}
                        onChange={(event) => {
                            update("domaineId", event.target.value);
                            update("themeId", "");
                        }}
                    >
                        <option value="">—</option>
                        {(domaines || []).map((domaine) => (
                            <option key={domaine.id} value={domaine.id}>
                                {domaine.nom || domaine.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="field">
                    <label>Thème</label>
                    <select value={form.themeId} onChange={(event) => update("themeId", event.target.value)}>
                        <option value="">—</option>
                        {(themesFiltered || []).map((theme) => (
                            <option key={theme.id} value={theme.id}>
                                {theme.nom || theme.name}
                            </option>
                        ))}
                    </select>
                </div>

                {!hideCampagneSelect && (
                    <div className="field">
                        <label>Campagne</label>
                        <select value={form.campagneId} onChange={(event) => update("campagneId", event.target.value)}>
                            <option value="">—</option>
                            {(campagnes || []).map((campagne) => (
                                <option key={campagne.id} value={campagne.id}>
                                    {campagne.nom || campagne.name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="field">
                    <label>Mode</label>
                    <select value={form.mode || ""} onChange={(event) => update("mode", event.target.value)}>
                        <option value="">—</option>
                        {MODE_OPTIONS.map((mode) => (
                            <option key={mode} value={mode}>
                                {mode}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="field">
                    <label>Période</label>
                    <select value={form.period || ""} onChange={(event) => update("period", event.target.value)}>
                        <option value="">—</option>
                        {PERIOD_OPTIONS.map((period) => (
                            <option key={period} value={period}>
                                {period}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="field">
                    <label>Niveau</label>
                    <select value={form.niveau || ""} onChange={(event) => update("niveau", event.target.value)}>
                        <option value="">—</option>
                        {NIVEAU_OPTIONS.map((niveau) => (
                            <option key={niveau} value={niveau}>
                                {niveau}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="field">
                    <label>Priorité</label>
                    <select value={form.priorite} onChange={(event) => update("priorite", event.target.value)}>
                        <option value={1}>1 - Haute</option>
                        <option value={2}>2 - Moyenne</option>
                        <option value={3}>3 - Basse</option>
                    </select>
                </div>

                <div className="field field-inline">
                    <label>Certifiante</label>
                    <input
                        type="checkbox"
                        checked={form.certifiante === "true"}
                        onChange={(event) => update("certifiante", event.target.checked ? "true" : "false")}
                    />
                </div>
            </div>

            <div className="field">
                <label>Objectif (1)</label>
                <textarea value={form.objectifs} onChange={(event) => update("objectifs", event.target.value)} />
            </div>

            <div className="field">
                <label>Objectif (2)</label>
                <textarea value={form.objectifs2} onChange={(event) => update("objectifs2", event.target.value)} />
            </div>

            <div className="field">
                <label>Objectif(3)</label>
                <textarea value={form.objectifs3} onChange={(event) => update("objectifs3", event.target.value)} />
            </div>

            <div className="field">
                <label>Description</label>
                <textarea
                    value={form.description}
                    onChange={(event) => update("description", event.target.value)}
                    rows={5}
                />
            </div>

            <div className="demande-actions">
                {onCancel && (
                    <button className="btn btn-light" type="button" onClick={onCancel} disabled={loading}>
                        Annuler
                    </button>
                )}
                <button className="btn btn-primary" type="submit" disabled={!campagneId || loading}>
                    Envoyer la demande
                </button>
            </div>

            {!campagneId && <div className="demande-hint">Sélectionnez d'abord une campagne.</div>}
            {demandesState.error && <div className="demande-hint">Une erreur est survenue lors de l'envoi de la demande.</div>}
        </form>
    );
}
